import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiLock,
  FiMail,
  FiUser,
  FiCheck,
  FiX,
  FiActivity,
  FiLayers,
  FiBarChart,
} from 'react-icons/fi';
import TermsModal from '../components/TermsModal';
import { fetchSignInMethodsForEmail } from 'firebase/auth';
import { registerUser } from '../firebase/authService';
import { auth } from '../firebase/config';
import { handleError } from '../utils/errorHandler';

const differentiators = [
  {
    icon: <FiActivity className="text-emerald-300" />,
    title: 'Real-time signals',
    description: 'Adaptive AI monitors volatility and surfaces smarter entries every minute.',
  },
  {
    icon: <FiLayers className="text-cyan-300" />,
    title: 'Modular strategies',
    description: 'Deploy ready-made playbooks or connect custom logic via our automation stack.',
  },
  {
    icon: <FiBarChart className="text-sky-300" />,
    title: 'Transparent analytics',
    description: 'Understand performance with deep attribution and compliance-ready exports.',
  },
];

const SignUpPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState('idle');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const navigate = useNavigate();

  // Check email availability using only Firebase Auth
  const checkEmailAvailability = async (email) => {
    if (!email) {
      setEmailStatus('idle');
      return;
    }
    
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setEmailStatus('checking');
    setError('');
    
    try {
      // Check if email exists in Firebase Auth
      const methods = await fetchSignInMethodsForEmail(auth, email);
      
      if (methods && methods.length > 0) {
        setEmailStatus('taken');
        setError('This email is already registered. Please sign in instead.');
      } else {
        setEmailStatus('available');
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailStatus('idle');
      // Only show error if it's not a permission denied error
      if (error.code !== 'permission-denied') {
        setError(handleError(error, 'Error checking email availability. Please try again.'));
      }
    }
  };

  // Check password strength
  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'email') {
      // Debounce email check
      const timer = setTimeout(() => {
        checkEmailAvailability(value);
      }, 500);
      return () => clearTimeout(timer);
    }
    
    if (name === 'password') {
      setPasswordStrength(checkPasswordStrength(value));
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    
    // Client-side validation
    if (!formData.fullName?.trim()) {
      setError('Please enter your full name');
      return;
    }
    
    if (!formData.email?.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    if (emailStatus !== 'available') {
      setError('Please verify your email is available before signing up');
      return;
    }
    
    if (!formData.password) {
      setError('Please enter a password');
      return;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match. Please make sure both passwords are identical.');
      return;
    }

    if (!agreedToTerms) {
      setError('Please agree to the Terms & Conditions before creating an account');
      return;
    }

    setIsLoading(true);
    
    try {
      const result = await registerUser(
        formData.email.toLowerCase().trim(),
        formData.password,
        formData.fullName.trim()
      );
      
      if (result?.success) {
        // Show success message and redirect
        setError('');
        alert('Account created successfully! You will be redirected to the dashboard.');
        navigate('/dashboard');
      } else {
        // Handle specific error cases
        const errorMessage = result?.error?.message || 
                           (result?.code ? `Error: ${result.code}` : 'Registration failed. Please try again.');
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Signup error:', {
        error,
        email: formData.email,
        timestamp: new Date().toISOString()
      });
      
      setError(handleError(error, 'An unexpected error occurred. Please try again later.'));
    } finally {
      setIsLoading(false);
    }
  };

  // Password strength indicator
  const getPasswordStrengthColor = () => {
    if (passwordStrength === 0) return 'bg-gray-500';
    if (passwordStrength <= 2) return 'bg-red-500';
    if (passwordStrength === 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const isSubmitDisabled = isLoading || emailStatus === 'checking' || !agreedToTerms;

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col px-4 py-10 sm:px-6 lg:flex-row lg:items-stretch lg:py-16">
        <div className="flex flex-1 flex-col justify-between rounded-3xl border border-slate-800/80 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 p-10 text-slate-100 shadow-[0_40px_120px_-40px_rgba(56,189,248,0.45)]">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-3 rounded-full border border-slate-700/60 bg-slate-900/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-300/90">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              Intelligent asset onboarding
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-50 sm:text-5xl">
              Create your Gemini AI Trading account
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-slate-300">
              Connect capital to adaptive automation, powerful analytics, and multi-market coverage with enterprise security.
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {differentiators.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-800/80 bg-slate-900/70 p-4 shadow-inner shadow-slate-900/30">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-800/80">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-100">{item.title}</p>
                    <p className="text-xs text-slate-400">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-slate-400">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-700/80 px-3 py-1">
              <span className="h-2 w-2 rounded-full bg-cyan-400" /> Multi-factor security ready
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-700/80 px-3 py-1">
              <span className="h-2 w-2 rounded-full bg-sky-400" /> 24/7 incident response
            </span>
          </div>
        </div>

        <div className="mt-10 w-full lg:mt-0 lg:ml-10 lg:w-[440px]">
          <div className="h-full rounded-3xl border border-slate-800/80 bg-slate-900/85 p-8 shadow-[0_25px_70px_-30px_rgba(59,130,246,0.45)] backdrop-blur-xl">
            <div className="text-center">
              <img
                src="/logo.png"
                alt="Gemini AI Trading"
                className="mx-auto mb-6 h-16 w-16"
              />
              <h2 className="text-2xl font-semibold text-slate-50">Create secure access</h2>
              <p className="mt-1 text-sm text-slate-400">Get started in minutes with full compliance controls</p>
            </div>

            {error && (
              <div className="mt-6 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                {error}
              </div>
            )}

            <form className="mt-8 space-y-5" onSubmit={handleSignUp}>
              <div className="space-y-1">
                <label htmlFor="fullName" className="text-sm font-medium text-slate-300">
                  Full name
                </label>
                <div className="relative">
                  <FiUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    id="fullName"
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Jane Doe"
                    className="w-full rounded-2xl border border-slate-700/70 bg-slate-950/70 py-3 pl-12 pr-4 text-sm text-slate-100 placeholder:text-slate-500 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/60"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label htmlFor="email" className="text-sm font-medium text-slate-300">
                  Work email
                </label>
                <div className="relative">
                  <FiMail className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@team.com"
                    className={`w-full rounded-2xl border bg-slate-950/70 py-3 pl-12 pr-12 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/60 ${
                      emailStatus === 'taken'
                        ? 'border-rose-500/60 focus:border-rose-400 focus:ring-rose-500/50'
                        : emailStatus === 'available'
                        ? 'border-emerald-500/60 focus:border-emerald-400'
                        : 'border-slate-700/70 focus:border-cyan-400'
                    }`}
                    required
                  />
                  {emailStatus === 'checking' && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-cyan-300" />
                    </div>
                  )}
                  {emailStatus === 'available' && (
                    <FiCheck className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-400" />
                  )}
                  {emailStatus === 'taken' && (
                    <FiX className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-400" />
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <label htmlFor="password" className="text-sm font-medium text-slate-300">
                  Password
                </label>
                <div className="relative">
                  <FiLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    id="password"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="At least 8 characters"
                    className="w-full rounded-2xl border border-slate-700/70 bg-slate-950/70 py-3 pl-12 pr-4 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/60"
                    required
                  />
                </div>
                {formData.password && (
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
                    <div
                      className={`h-full ${getPasswordStrengthColor()}`}
                      style={{ width: `${(passwordStrength / 4) * 100}%` }}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-slate-300">
                  Confirm password
                </label>
                <div className="relative">
                  <FiLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Re-enter password"
                    className={`w-full rounded-2xl border bg-slate-950/70 py-3 pl-12 pr-4 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/60 ${
                      formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword
                        ? 'border-rose-500/60 focus:border-rose-400 focus:ring-rose-500/60'
                        : 'border-slate-700/70 focus:border-cyan-400'
                    }`}
                    required
                  />
                </div>
              </div>

              <div className="flex items-start gap-3 text-xs text-slate-400">
                <input
                  id="agreeTerms"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(event) => setAgreedToTerms(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-600 bg-transparent text-cyan-400 focus:ring-cyan-500"
                />
                <label htmlFor="agreeTerms" className="leading-relaxed">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-cyan-300 underline transition hover:text-cyan-200"
                  >
                    Terms & Conditions
                  </button>{' '}
                  and{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-cyan-300 underline transition hover:text-cyan-200"
                  >
                    Privacy Policy
                  </button>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitDisabled}
                className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-400 via-emerald-400 to-cyan-400 py-3 text-sm font-bold text-slate-900 shadow-[0_0_30px_rgba(34,211,238,0.5),0_0_60px_rgba(16,185,129,0.3)] transition-all hover:shadow-[0_0_40px_rgba(34,211,238,0.7),0_0_80px_rgba(16,185,129,0.5)] hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 animate-pulse ${
                  isSubmitDisabled ? 'cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-slate-900" />
                    Creating account…
                  </div>
                ) : (
                  <>
                    <FiCheck className="text-lg" /> Create Secure Account
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-400">
              Already have an account?{' '}
              <Link
                to="/signin"
                className="font-semibold text-cyan-200 transition hover:text-cyan-100"
              >
                Sign in here
              </Link>
            </div>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-700/70 px-5 py-2 text-xs font-medium text-slate-400 transition hover:border-slate-500 hover:text-slate-200"
              >
                <FiArrowLeft /> Back to landing
              </button>
            </div>
          </div>
        </div>
      </div>

      <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </div>
  );
}

export default SignUpPage;
